'use client'

import * as React from 'react'

import cn from '@config/tailwind/cn'

import type { MediaType } from './types'
import { typographyVariants } from '../typography'

interface FeatureItem {
  icon?: MediaType | string
  title: string
  description: string
  id?: string
}

interface FeatureGridBlockFields {
  blockType: 'feature-grid'
  title: string
  subtitle?: string
  subtitleHighlight?: string
  description?: string
  items?: FeatureItem[]
}

interface FeatureGridBlockProps {
  fields: FeatureGridBlockFields
}

export function FeatureGridBlock({ fields }: FeatureGridBlockProps) {
  const { title, subtitle, subtitleHighlight, description, items } = fields

  return (
    <div className='ui:my-8 ui:py-10 ui:text-center'>
      {/* Section header */}
      <div className='ui:mx-auto ui:max-w-full ui:flex ui:flex-col ui:gap-1 ui:p-6'>
        <h2 className={cn(typographyVariants({ variant: 'headline-lg' }), 'ui:text-neutral-black ui:mb-4')}>{title}</h2>

        {(!!subtitle || !!subtitleHighlight) && (
          <p className={cn(typographyVariants({ variant: 'body-lg' }), 'ui:text-neutral-black ui:mb-2')}>
            {subtitle && <span>{subtitle} </span>}
            {subtitleHighlight && <strong className='ui:font-semibold'>{subtitleHighlight}</strong>}
          </p>
        )}

        {description && (
          <p className={cn(typographyVariants({ variant: 'body-lg' }), 'ui:text-neutral-black')}>{description}</p>
        )}
      </div>

      {/* Feature items grid */}
      {items && items.length > 0 && (
        <div className='ui:mt-10 ui:grid ui:grid-cols-1 ui:lg:grid-cols-3 ui:gap-6 ui:p-6'>
          {items.map((item, index) => {
            const icon = typeof item.icon === 'object' ? item.icon : undefined

            return (
              <div key={item.id ?? index} className='ui:flex ui:flex-col ui:items-center ui:gap-3'>
                {icon?.url && (
                  <div className='ui:flex ui:size-18 ui:items-center ui:justify-center'>
                    <img
                      src={icon.url}
                      alt={icon.alt ?? item.title}
                      className='ui:h-full ui:w-full ui:object-contain'
                      loading='lazy'
                    />
                  </div>
                )}

                <p
                  className={cn(
                    typographyVariants({ variant: 'headline-sm' }),
                    'ui:text-neutral-black ui:font-semibold',
                  )}
                >
                  {item.title}
                </p>

                <p className={cn(typographyVariants({ variant: 'body-lg' }), 'ui:text-neutral-black ui:text-center')}>
                  {item.description}
                </p>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
